import { useEffect, useRef } from 'react';
import { useDialogueStore } from '@/state/dialogueStore';
import type { LevelId } from '@/state/types';
import { levelIdKey } from '@/levels/LevelController';

export const SLOW_PLAY_IDLE_MS = 15_000;

const IDLE_EVENTS: readonly (keyof WindowEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'wheel',
  'touchstart',
];

/**
 * Fires the current level's `slowPlay` dialogue line after 15s of user
 * inactivity. Each activity event resets the timer; the hook re-arms
 * after every fire so a long idle produces a rotating stream of lines.
 */
export function useSlowPlay(id: LevelId, enabled = true): void {
  const key = levelIdKey(id);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const clear = () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const arm = () => {
      clear();
      timeoutRef.current = window.setTimeout(() => {
        // Do not fire slowPlay if we are currently at an advance gate (pendingAdvance).
        // This prevents idle dialogue from overwriting solution or failure lines.
        const isPending = useDialogueStore.getState().pendingAdvance;
        if (!isPending) {
          useDialogueStore.getState().fire('slowPlay');
        }
        arm();
      }, SLOW_PLAY_IDLE_MS);
    };

    const onActivity = () => {
      arm();
    };

    arm();
    for (const ev of IDLE_EVENTS) {
      window.addEventListener(ev, onActivity, { passive: true });
    }

    return () => {
      clear();
      for (const ev of IDLE_EVENTS) {
        window.removeEventListener(ev, onActivity);
      }
    };
  }, [enabled, key]);
}
